import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { initials as initialsOf } from '../../lib/format';

/* Avatar del remitente.
   Orden: foto del compañero (dominio interno) → logo de la empresa (dominio
   externo) → iniciales sobre tono estable. Las imágenes las sirve NUESTRO
   backend (nunca el navegador a terceros), así que no filtramos con quién se
   cartea el usuario. */

/* Tonos derivados de los tokens de etiqueta (contrato de diseño). */
const AVATAR_TONES = [
  ['var(--accent-soft)', 'var(--accent)'],
  ['var(--l-client-bg)', 'var(--l-client)'],
  ['var(--l-inv-bg)', 'var(--l-inv)'],
  ['var(--l-proj-bg)', 'var(--l-proj)'],
  ['var(--hover)', 'var(--text-2)'],
];

export function avatarTone(seed) {
  const s = seed || '';
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 997;
  return AVATAR_TONES[Math.abs(h) % AVATAR_TONES.length];
}

/* Caché a nivel de módulo: una petición por dominio/persona en toda la sesión.
   El valor es una promesa (evita peticiones duplicadas cuando 50 filas piden lo
   mismo a la vez) que resuelve a un objectURL o a null (404 recordado). */
const cache = new Map();

const PERSONAL_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'hotmail.com', 'hotmail.es', 'outlook.com',
  'outlook.es', 'live.com', 'live.com.mx', 'msn.com', 'yahoo.com', 'yahoo.com.mx',
  'yahoo.es', 'icloud.com', 'me.com', 'aol.com', 'proton.me', 'protonmail.com',
  'pm.me', 'zoho.com', 'gmx.com', 'mail.com', 'yandex.com',
]);

function domainOf(email) {
  const at = (email || '').lastIndexOf('@');
  return at === -1 ? '' : email.slice(at + 1).toLowerCase().trim();
}

/* Dominios de la ORGANIZACIÓN (todos los permitidos, `/org/dominios`): un compañero de @huntersecurity.mx es de la casa
   aunque mis cuentas sean de @orvian.mx. Antes solo contaban los dominios de mis cuentas y salía el logo de la empresa
   en vez de su foto (2026-10-06). Una petición por sesión; si falla, se reintenta en el siguiente avatar. */
let casaCache = null;
let casaPromesa = null;
function dominiosCasa() {
  if (!casaPromesa) {
    casaPromesa = api.get('/org/dominios')
      .then((r) => { casaCache = (r.data?.dominios || []).map((d) => String(d).toLowerCase()); return casaCache; })
      .catch(() => { casaPromesa = null; return []; });
  }
  return casaPromesa;
}

function fetchImage(key, url, params) {
  if (cache.has(key)) return cache.get(key);
  const p = api
    .get(url, { params, responseType: 'blob' })
    .then((r) => (r.data && r.data.size ? URL.createObjectURL(r.data) : null))
    .catch(() => null); // 404 incluido: se recuerda para no reintentar
  cache.set(key, p);
  return p;
}

/**
 * @param {string} email    correo del remitente
 * @param {string} name     nombre para las iniciales
 * @param {number} size     diámetro en px (38 lista, 44 lector)
 * @param {string[]} internalDomains  dominios propios del usuario (sus cuentas)
 */
export default function SenderAvatar({
  email, name, size = 38, internalDomains = [], className = '', style = {},
}) {
  const [src, setSrc] = useState(null);
  const [isLogo, setIsLogo] = useState(false);
  const [casa, setCasa] = useState(casaCache);
  useEffect(() => {
    if (casa) return undefined;
    let vivo = true;
    dominiosCasa().then((l) => { if (vivo) setCasa(l); });
    return () => { vivo = false; };
  }, [casa]);

  const addr = (email || '').toLowerCase().trim();
  const domain = domainOf(addr);
  const propios = [...internalDomains, ...(casa || [])];
  // Propio = el dominio exacto o un subdominio suyo (email.zunoid.com.mx), de mis cuentas o de la organización.
  const internal = !!domain && propios.some((d) => {
    const own = (d || '').toLowerCase();
    return own && (domain === own || domain.endsWith(`.${own}`));
  });

  useEffect(() => {
    let alive = true;
    setSrc(null);
    setIsLogo(false);
    if (!addr || !domain) return undefined;

    let promise;
    let logo = false;
    if (internal) {
      promise = fetchImage(`u:${addr}`, '/users/photo', { email: addr });
    } else if (!PERSONAL_DOMAINS.has(domain)) {
      // Sin la lista de dominios propios aún cargada no se puede distinguir a un
      // compañero de una empresa: esperamos, en vez de pedir el logo y acabar
      // pintando el logotipo de la casa a toda la plantilla.
      if (!internalDomains.length || casa === null) return undefined;
      logo = true;
      promise = fetchImage(`d:${domain}`, '/brand/logo', { domain });
    } else {
      return undefined; // correo personal: iniciales, sin pedir nada
    }

    promise.then((url) => {
      if (!alive || !url) return;
      setSrc(url);
      setIsLogo(logo);
    });
    return () => { alive = false; };
    // Los objectURL viven en la caché del módulo (compartidos entre filas), por
    // eso NO se revocan al desmontar: se reutilizan mientras dure la sesión.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addr, domain, internal, internalDomains.length, casa === null]);

  const [bg, fg] = avatarTone(addr || name);
  const base = {
    width: size, height: size, borderRadius: '50%', flex: 'none',
    ...style,
  };

  if (src) {
    return (
      <div
        className={`grid place-items-center overflow-hidden ${className}`}
        style={{
          ...base,
          background: isLogo ? 'var(--panel-2)' : 'var(--hover)',
          border: isLogo ? '1px solid var(--line)' : 'none',
        }}
        aria-hidden="true"
      >
        <img
          src={src}
          alt=""
          style={{
            width: '100%', height: '100%',
            objectFit: isLogo ? 'contain' : 'cover',
            padding: isLogo ? Math.max(3, Math.round(size * 0.1)) : 0,
          }}
        />
      </div>
    );
  }

  return (
    <div
      className={`grid place-items-center font-semibold select-none ${className}`}
      style={{ ...base, background: bg, color: fg, fontSize: Math.round(size * 0.34) }}
      aria-hidden="true"
    >
      {initialsOf(name, email)}
    </div>
  );
}
